// src/components/contacts/useContacts.js
import { useState, useEffect } from 'react';
import api from '../../api';
import { useAuth } from '../../authContext';

export const useContacts = () => {
  const { token, role, userId } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hilfsfunktion: Prüft, ob ein Kontakt den Namen "Allgemein" hat
  const isAllgemeinContact = (firstName, lastName = '') => {
    const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
    return fullName === 'allgemein';
  };

  // --- Daten abrufen (Kontakte + Unternehmen) ---
  const fetchContactsAndCompanies = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [contactsRes, companiesRes] = await Promise.all([
        api.get('/contacts', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get('/companies', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setContacts(contactsRes.data || []);
      setCompanies(companiesRes.data || []);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Ansprechpartner oder Unternehmen. Bitte versuche es später erneut.');
      console.error('Fehler:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactsAndCompanies();
  }, [token]);

  // --- Toggle-Handler für Status (nur für Admins) ---
  const toggleContactStatus = async (contactId, currentStatus) => {
    if (role !== 'Admin') {
      throw new Error('Nur Admins können den Status ändern.');
    }

    // Prüfe, ob der Kontakt ein Standardkontakt ist und auf "Inaktiv" gesetzt werden soll
    const contact = contacts.find((c) => c.id === contactId);
    if (contact?.isDefault && currentStatus) {
      throw new Error('Standardkontakte dürfen nicht deaktiviert werden.');
    }

    try {
      await api.put(
        `/contacts/${contactId}/active?isActive=${!currentStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContacts(
        contacts.map((contact) =>
          contact.id === contactId ? { ...contact, active: !currentStatus } : contact
        )
      );
    } catch (err) {
      console.error(`Fehler beim Ändern des Status:`, err);
      throw err;
    }
  };

  // --- Add Contact ---
  const addContact = async (contactData) => {
    if (isAllgemeinContact(contactData.firstName, contactData.lastName)) {
      throw new Error('Ein Kontakt mit dem Namen "Allgemein" kann nicht manuell angelegt werden. Dieser wird automatisch bei der Neuanlage einer Company erstellt.');
    }

    try {
      const response = await api.post(
        '/contacts',
        {
          ...contactData,
          companyId: parseInt(contactData.companyId),
          active: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const addedContact = {
        ...contactData,
        id: response.data.id,
        companyId: parseInt(contactData.companyId),
        active: true,
      };
      setContacts([...contacts, addedContact]);
      return addedContact;
    } catch (err) {
      console.error('Fehler beim Erstellen des Ansprechpartners:', err);
      throw err;
    }
  };

  // --- Edit Contact ---
  const editContact = async (contactId, contactData) => {
    const originalContact = contacts.find((c) => c.id === contactId);
    const originalIsAllgemein = isAllgemeinContact(originalContact.firstName, originalContact.lastName);
    const newIsAllgemein = isAllgemeinContact(contactData.firstName, contactData.lastName);

    // Blockieren, wenn der Kontakt NICHT ursprünglich "Allgemein" war, aber jetzt zu "Allgemein" geändert wird
    if (!originalIsAllgemein && newIsAllgemein) {
      throw new Error('Ein Kontakt mit dem Namen "Allgemein" kann nicht manuell umbenannt werden.');
    }

    try {
      await api.put(
        `/contacts/${contactId}`,
        {
          ...contactData,
          companyId: parseInt(contactData.companyId),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContacts(
        contacts.map((contact) =>
          contact.id === contactId
            ? { ...contactData, id: contactId, companyId: parseInt(contactData.companyId) }
            : contact
        )
      );
    } catch (err) {
      console.error('Fehler beim Bearbeiten des Ansprechpartners:', err);
      throw err;
    }
  };

  // --- Add Task (optional: könnte auch in einen eigenen Hook `useTasks` ausgelagert werden) ---
  const addTask = async (taskData) => {
    try {
      const response = await api.post(
        '/tasks',
        {
          ...taskData,
          ContactId: parseInt(taskData.ContactId),
          UserId: userId,
          Status: 'TODO',
          CreatedAt: new Date().toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      console.error('Fehler beim Erstellen der Aufgabe:', err);
      throw err;
    }
  };

  return {
    contacts,
    companies,
    loading,
    error,
    fetchContactsAndCompanies,
    toggleContactStatus,
    addContact,
    editContact,
    addTask,
    isAllgemeinContact,
  };
};

export default useContacts;